import React, { useContext } from 'react';
import { graphql } from 'gatsby';
import FilteredContent from './FilteredContent';
import {
    KontentNumberValue,
    KontentRadioMultiChoice,
    KontentStringValue,
} from '../../../types/KontentProps';
import { useResearch } from '../Global/DataUtils/researchQueries';
import { BlogPagePreview, useBlogs } from '../Global/DataUtils/blogQueries';
import { useTaxonomy } from '../Global/DataUtils/taxonomyQueries';
import { getMultiChoice } from '../Global/DataUtils';
import {
    Publication,
    usePublications,
} from '../Global/DataUtils/publicationQueries';
import { Faq, useFaqs } from '../Global/DataUtils/faqQueries';
import { useSupport } from '../Global/DataUtils/supportQueries';
import { PageMetadataContext } from '../../templates/PageMetaDataContext';
interface Tag {
    codename: string;
    name: string;
}

export interface TagCategory {
    [category: string]: Tag[];
}

type TaxonomyCategory = {
    elements: {
        associated_taxonomy_code_name: {
            value: string;
        };
    };
};

export type ContentPreview = BlogPagePreview | Publication | Faq;

type ContentTypes =
    | 'blog'
    | 'product'
    | 'support'
    | 'case_study'
    | 'job_openings';
type Layouts = 'blog' | 'news_and_media' | 'faqs';

export interface FilteredContentProps {
    kontentItemId?: string;
    headline: KontentStringValue;
    use_grid_view: KontentRadioMultiChoice<'grid' | 'stacked'>;
    content_type: KontentRadioMultiChoice<ContentTypes>;
    cta_label: KontentStringValue;
    desktop_value_to_show: KontentNumberValue;
    mobile_value_to_show: KontentNumberValue;
    layout: KontentRadioMultiChoice<Layouts>;
    view_more_label?: KontentStringValue;
    view_more_value_desktop?: KontentNumberValue;
    view_more_value_mobile?: KontentNumberValue;
    featured_tiles_headline?: KontentStringValue;
    reveal_descriptions?: KontentStringValue;
    center_item?: {
        value: Publication[];
    };
    featured_item?: {
        value: ContentPreview[];
    };
    tag_categories_for_display: {
        value: TaxonomyCategory[];
    };
    typeform_url?: {
        value: string;
    };
    show_blog_subscription_form?: KontentRadioMultiChoice<'yes' | 'no'>;
    submit_cta_label?: KontentStringValue;
    interested_in_field?: KontentStringValue;
    full_name_field?: KontentStringValue;
    form_headline?: KontentStringValue;
    email_address_field?: KontentStringValue;
    blog_topic: {
        value: [
            {
                codename: string;
            },
            {
                codename: string;
            }
        ];
    };
    form_confirmation_message?: KontentStringValue;
    featured_tiles?: {
        value: Publication[];
    };
    faq_featured_tiles?: {
        value: Faq[];
    };
}

const FilteredContentContainer: React.FC<FilteredContentProps> = ({
    ...props
}) => {
    const taxonomies = useTaxonomy();
    const pageMetadata = useContext(PageMetadataContext);

    const allPublications = usePublications();
    const allFaqs = useFaqs();
    const allSupport = useSupport();
    const allResearch = useResearch();
    const allBlogs = useBlogs(pageMetadata.preferredLanguage);

    const layout = getMultiChoice(props.layout);
    if (!layout) return null;

    let tagCategoriesForDisplay = props.tag_categories_for_display.value?.map(
        (tagCategory) => {
            let assocTaxonomyCodename =
                tagCategory.elements.associated_taxonomy_code_name.value;
            const assocTaxonomy = taxonomies.filter(
                (taxonomyNode) =>
                    taxonomyNode.node.system.codename === assocTaxonomyCodename
            );

            return { [assocTaxonomyCodename]: assocTaxonomy[0]?.node?.terms };
        }
    );
    tagCategoriesForDisplay = tagCategoriesForDisplay.map((category) => {
        if (category.search_topics_my) {
            category.topics = category.search_topics_my;
            delete category.search_topics_my;
        }
        return category;
    });

    const getContent = () => {
        const contentType = getMultiChoice(props.content_type);

        const applyContentFilters = (
            content: ContentPreview[],
            options: { contentType?: string } = {}
        ) => {
            return content.filter((contentItem: ContentPreview) => {
                const isNotFeatured =
                    !props.featured_item?.value[0] ||
                    contentItem.id !== props.featured_item?.value[0]?.id;

                const isPreferredLanguage =
                    contentItem.preferred_language?.toLowerCase() ===
                    pageMetadata.preferredLanguage?.toLowerCase();

                const isNotSelf =
                    !contentItem.elements.slug?.value ||
                    contentItem.elements.slug?.value !== pageMetadata.slug;

                const isNotCenter =
                    !props.center_item?.value[0] ||
                    contentItem.id !== props.featured_item?.value[0]?.id;

                const isCorrectContentType =
                    !options?.contentType ||
                    options?.contentType ===
                        getMultiChoice(contentItem.elements.content_type);

                let isNotFeaturedTile = true;
                if (
                    props?.featured_tiles != undefined &&
                    Array.isArray(props?.featured_tiles.value) &&
                    props?.featured_tiles.value.length > 0
                ) {
                    isNotFeaturedTile = !props?.featured_tiles.value.some(
                        (item) => item.id === contentItem.id
                    );
                }

                return (
                    isNotFeatured &&
                    isPreferredLanguage &&
                    isNotSelf &&
                    isNotCenter &&
                    isCorrectContentType &&
                    isNotFeaturedTile
                );
            });
        };

        if (layout === 'news_and_media' || contentType === 'publication') {
            return applyContentFilters(allPublications, { contentType });
        }

        if (layout === 'faqs' || contentType === 'faq') {
            return applyContentFilters(allFaqs, { contentType });
        }

        switch (contentType) {
            case 'product':
                break;

            case 'support':
                return applyContentFilters(allSupport);

            case 'case_study':
                return applyContentFilters(allResearch);

            default:
                return applyContentFilters(allBlogs);
        }
    };

    const content = getContent();

    if (!content) {
        return null;
    }

    return (
        <FilteredContent
            {...props}
            tagCategories={tagCategoriesForDisplay}
            content={content}
            filter={pageMetadata?.location?.state?.filter}
        />
    );
};

export default FilteredContentContainer;

export const fragmentFilteredContent = graphql`
    fragment FilteredContent on kontent_item_component___filtered_content {
        elements {
            headline {
                value
            }
            use_grid_view {
                value {
                    codename
                }
            }
            content_type: content_type_a20cc11 {
                value {
                    codename
                    name
                }
            }
            cta_label {
                value
            }
            desktop_value_to_show {
                value
            }
            layout {
                value {
                    codename
                }
            }
            mobile_value_to_show {
                value
            }
            view_more_label {
                value
            }
            view_more_value_desktop {
                value
            }
            view_more_value_mobile {
                value
            }
            featured_tiles_headline {
                value
            }
            reveal_descriptions {
                value
            }
            center_item {
                value {
                    ...kontentPublication
                }
            }
            featured_item {
                value {
                    ... on kontent_item_layout___master_page {
                        ...blogPagePreview
                    }
                    ...kontentPublication
                }
            }
            tag_categories_for_display {
                value {
                    ... on kontent_item_taxonomy_category {
                        id
                        elements {
                            associated_taxonomy_code_name {
                                value
                            }
                        }
                    }
                }
            }
            typeform_url {
                value
            }
            show_blog_subscription_form {
                value {
                    codename
                }
            }
            submit_cta_label {
                value
            }
            interested_in_field {
                value
            }
            full_name_field {
                value
            }
            form_headline {
                value
            }
            email_address_field {
                value
            }
            blog_topic {
                value {
                    codename
                }
            }
            form_confirmation_message {
                value
            }
            faq_featured_tiles {
                value {
                    ... on kontent_item_content_item___faq {
                        id
                        preferred_language
                        elements {
                            question {
                                value
                            }
                            answer {
                                ...kontentRichText
                            }
                            content_type {
                                value {
                                    codename
                                    name
                                }
                            }
                            department {
                                value {
                                    codename
                                    name
                                }
                            }
                            blog_topic {
                                value {
                                    codename
                                    name
                                }
                            }
                            country {
                                value {
                                    codename
                                    name
                                }
                            }
                            wellness_type {
                                value {
                                    codename
                                    name
                                }
                            }
                            product_type {
                                value {
                                    codename
                                    name
                                }
                            }
                            product_family {
                                value {
                                    codename
                                    name
                                }
                            }
                            topics {
                                value {
                                    codename
                                    name
                                }
                            }
                            cta_configuration__cta_window_action {
                                value {
                                    codename
                                }
                            }
                            cta_configuration__cta_location_anchor_point {
                                value
                            }
                            cta_configuration__cta_location {
                                ...kontentCtaLocation
                            }
                            cta_configuration__cta_label {
                                value
                            }
                        }
                    }
                }
            }
            featured_tiles {
                value {
                    ... on kontent_item_content_item___publication {
                        id
                        elements {
                            blog_topic {
                                value {
                                    name
                                }
                                type
                                name
                            }
                            content_type {
                                value {
                                    name
                                }
                                type
                                name
                            }
                            country {
                                value {
                                    name
                                }
                                type
                                name
                            }
                            title {
                                value
                            }
                            publication_date {
                                value
                            }
                            source {
                                value
                            }
                            short_description {
                                value
                            }
                            image {
                                value {
                                    ... on kontent_item_media___image {
                                        id
                                        elements {
                                            file {
                                                value {
                                                    url
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            publication_logo {
                                value {
                                    ... on kontent_item_media___image {
                                        id
                                        elements {
                                            file {
                                                value {
                                                    url
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            url_location {
                                value
                            }
                            wellness_type {
                                value {
                                    name
                                }
                                type
                                name
                            }
                            document {
                                value {
                                    ... on kontent_item_document___general {
                                        id
                                        elements {
                                            document {
                                                value {
                                                    url
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;
